import { Card, Divider, Group, LoadingOverlay, Switch, Tabs, Text } from '@mantine/core'
import { Fragment } from 'react'
import { Client } from '@/api/controlUnitApi/client'
import { CameraPosition } from '@/api/controlUnitApi/types.gen'
import useOtherPanelData from './useOtherPanelData'
import LevelControl from './LevelControl'
import { CAMERA_KEYS, IR_LIGHT_KEYS, IR_LIGHT_POSITIONS } from './helpers'
import { useTranslation } from 'react-i18next'

const zoomLevels = [
  { label: '10', value: String(10) },
  { label: '20', value: String(20) },
  { label: '50', value: String(50) },
  { label: '100', value: String(100) },
  { label: '200', value: String(200) },
]

const apertureLevels = [
  { label: '1', value: String(1) },
  { label: '2', value: String(2) },
  { label: '5', value: String(5) },
  { label: '10', value: String(10) },
  { label: '20', value: String(20) },
]

const infraredLevels = [
  { label: '1', value: String(1) },
  { label: '2', value: String(2) },
  { label: '5', value: String(5) },
  { label: '10', value: String(10) },
  { label: '20', value: String(20) },
]

const OtherPanel = ({
  source,
  cameras,
  client,
  isRefetchingCombinedView,
}: {
  source: string
  cameras: CameraPosition[]
  client: Client
  isRefetchingCombinedView: boolean
}) => {
  const { t } = useTranslation(['hardware', 'common'])
  const {
    zoomLevels: zoomData,
    apertureLevels: apertureData,
    irLightLevels: irLightData,
    irCutFilters: irCutFilterData,
    mutateZoom,
    mutateAperture,
    mutateIrLight,
    mutateIrCutFilter,
    isRefetching,
  } = useOtherPanelData({ client, source, cameras, isRefetchingCombinedView })

  return (
    <Tabs.Panel value="other">
      <Card shadow="sm" p="lg" radius="md" withBorder mt="lg">
        <LoadingOverlay visible={isRefetching} />
        {cameras.map(position => (
          <Fragment key={`zoom-${position}`}>
            <LevelControl
              label={t(CAMERA_KEYS[position].zoom)}
              value={zoomData[position]}
              min={0}
              max={4073}
              increments={zoomLevels}
              defaultIncrement={50}
              threshold={50}
              placeholder={t('zoomPlaceholder')}
              disabled={zoomData[position] === undefined || isRefetching}
              onCommit={value => mutateZoom({ position, value })}
            />
            <Divider />
          </Fragment>
        ))}

        {cameras.map(position => (
          <Fragment key={`aperture-${position}`}>
            <LevelControl
              label={t(CAMERA_KEYS[position].aperture)}
              value={apertureData[position]}
              min={0}
              max={75}
              increments={apertureLevels}
              defaultIncrement={5}
              threshold={10}
              placeholder={t('aperturePlaceholder')}
              disabled={apertureData[position] === undefined || isRefetching}
              onCommit={value => mutateAperture({ position, value })}
            />
            <Divider />
          </Fragment>
        ))}

        {IR_LIGHT_POSITIONS.map(position => (
          <Fragment key={`ir-light-${position}`}>
            <LevelControl
              label={t(IR_LIGHT_KEYS[position].infrared)}
              value={irLightData[position]}
              min={0}
              max={255}
              increments={infraredLevels}
              defaultIncrement={5}
              threshold={20}
              placeholder={t('infraredPlaceholder')}
              disabled={irLightData[position] === undefined || isRefetching}
              onCommit={value => mutateIrLight({ position, value })}
            />
            <Divider />
          </Fragment>
        ))}

        <Text mb="xs" mt="md">
          {t('irCutFilterEnabled')}
        </Text>
        <Group justify="flex-start" mb="md">
          {cameras.map(position => (
            <Switch
              key={`ir-cut-${position}`}
              label={t(CAMERA_KEYS[position].label)}
              checked={irCutFilterData[position] ?? false}
              disabled={irCutFilterData[position] === undefined || isRefetching}
              onChange={event => mutateIrCutFilter({ position, enabled: event.currentTarget.checked })}
            />
          ))}
        </Group>
      </Card>
    </Tabs.Panel>
  )
}

export default OtherPanel
